/**
 * User menu in the sidebar footer.
 *
 * Trigger is the user-name block; the popover lists:
 *   • Per-app settings (Tasks, Health, …)
 *   • Profile settings
 *   • Platform settings (admin only)
 *   • Sign out (visually distinct)
 *
 * App switching lives in the brand-row AppSwitcher, not here.
 */

import { useNavigate } from '@tanstack/react-router';
import * as Popover from '@radix-ui/react-popover';
import { Icon } from '@k-os/ui';
import type { AuthUser } from '../api/auth';
import styles from './UserMenu.module.css';

export interface UserMenuProps {
  user: Pick<AuthUser, 'displayName' | 'platformRole'>;
  trigger: React.ReactNode;
  onSignOut: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  to: string;
  icon: 'settings' | 'inbox' | 'today';
}

export function UserMenu({ user, trigger, onSignOut }: UserMenuProps) {
  const navigate = useNavigate();
  const isAdmin = user.platformRole === 'admin';

  const items: MenuItem[] = [];
  if (isAdmin) {
    items.push({ id: 'platform', label: 'Platform settings', to: '/settings/platform/approvals', icon: 'settings' });
  }
  items.push({ id: 'tasks', label: 'K-OS Tasks settings', to: '/settings/tasks', icon: 'inbox' });
  items.push({ id: 'profile', label: 'Profile settings', to: '/settings/profile', icon: 'today' });

  return (
    <Popover.Root>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={styles.content}
          side="bottom"
          align="center"
          sideOffset={8}
          collisionPadding={20}
        >
          <div className={styles.header}>{user.displayName}</div>
          <div className={styles.list}>
            {items.map((it) => (
              <Popover.Close key={it.id} asChild>
                <button
                  type="button"
                  className={styles.item}
                  onClick={() => void navigate({ to: it.to })}
                >
                  <span className={styles.itemIcon}>
                    <Icon name={it.icon} size={13} />
                  </span>
                  <span>{it.label}</span>
                </button>
              </Popover.Close>
            ))}
          </div>
          <div className={styles.divider} />
          <Popover.Close asChild>
            <button
              type="button"
              className={`${styles.item} ${styles.signOut}`}
              onClick={onSignOut}
            >
              <span className={styles.itemIcon}>
                <SignOutIcon />
              </span>
              <span>Sign out</span>
            </button>
          </Popover.Close>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function SignOutIcon() {
  // Inline so we don't have to add it to the shared Icon set just for this.
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
